// @ts-nocheck
import { useEffect } from "react";
import { toast } from "sonner";
import socket from "../utilities/adminSocket";
import { Storage } from "../utilities/setting";
import { USER_ROLES } from "../utilities/constant";

// Matches legacy cloudtelephony_frontend's useAdminBreakNotifications.js
// ADMIN_ROLES exactly — this set was missing RESELLER/COMPANY_ADMIN,
// so those roles never got admin break/follow-up notifications even
// once this hook is wired up.
const ADMIN_ROLES = new Set([
  USER_ROLES.SUPERADMIN,
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.SUB_SUPERADMIN,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
  USER_ROLES.RESELLER,
  USER_ROLES.COMPANY_ADMIN,
]);

const formatScheduledAt = (payload) => {
  const iso = payload?.scheduled_at;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  }
  const date = payload?.follow_up_date || "";
  const time = payload?.follow_up_time || "";
  return `${date} ${time}`.trim() || "scheduled time";
};

function formatExceededFor(payload) {
  if (!payload?.scheduled_end_at) return "";
  const scheduled = new Date(payload.scheduled_end_at).getTime();
  const now = Date.now();
  const secs = Math.max(0, Math.floor((now - scheduled) / 1000));
  if (secs < 60) return `${secs}s over`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return rem ? `${mins}m ${rem}s over` : `${mins}m over`;
}

function useAdminBreakNotifications() {
  useEffect(() => {
    let user;
    try {
      user = JSON.parse(Storage.getItem("user") || "null");
    } catch (e) {
      return undefined;
    }

    if (!user?.id) return undefined;

    const isAdmin = ADMIN_ROLES.has(user?.role);
    const adminId = user.id;

    const refetchNotifications = () =>
      window.dispatchEvent(new CustomEvent("cti:notification_refresh", { detail: {} }));


    const handleConnect = () => {
      if (isAdmin) socket.emit("join_admin", { adminId });
      refetchNotifications();
    };

    const handleReconnect = () => {
      if (isAdmin) socket.emit("join_admin", { adminId });
    };

    const handleConnectError = (err) => {
      console.log("Admin socket connection error:", err?.message);
    };

    const handleBreakExceeded = (payload) => {
      const agentName = payload?.agent?.name || "Agent";
      const breakTitle = payload?.break?.title || "Break";
      const allowed = payload?.break?.allowed_duration_minutes;
      const overBy = formatExceededFor(payload);

      const message =
        `${agentName} has exceeded the "${breakTitle}" break` +
        (allowed ? ` (allowed ${allowed} min).` : "")

      toast.warning(message, {
        autoClose: 8000,
        toastId: `break-exceeded-${payload?.userBreakId || agentName}`,
      });
    };

    const handleFollowUpMissed = (payload) => {
      const agentName = payload?.agent?.name || "Agent";
      const contact = payload?.did_number || "a contact";
      const message =
        `${agentName} missed a follow-up with ${contact} scheduled at ${formatScheduledAt(
          payload
        )}.`;

      toast.warning(message, {
        autoClose: 10000,
        toastId: `follow-up-missed-${payload?.followUpId || agentName}`,
      });
    };

    const handleNotificationRefresh = (payload) => {
      window.dispatchEvent(
        new CustomEvent("cti:notification_refresh", { detail: payload || {} })
      );
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.io.on("reconnect", handleReconnect);
    if (isAdmin) {
      socket.on("break_exceeded", handleBreakExceeded);
      socket.on("follow_up_missed", handleFollowUpMissed);
    }
    socket.on("notification_refresh", handleNotificationRefresh);

    if (!socket.connected) {
      socket.connect();
    } else if (isAdmin) {
      socket.emit("join_admin", { adminId });
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.io.off("reconnect", handleReconnect);
      socket.off("break_exceeded", handleBreakExceeded);
      socket.off("follow_up_missed", handleFollowUpMissed);
      socket.off("notification_refresh", handleNotificationRefresh);
    };
  }, []);
}

export default useAdminBreakNotifications;
